import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import IRoomType from '../../../main/interfaces/IRoomType';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import BasicCli from '../../../main/components/basicCli/BasicCli';
import GeneralTable from '../../../main/components/grid/GeneralTable';
import GeneralModal from '../../../main/components/modal/general/GeneralModal';
import GeneralInput from '../../../main/components/generalInput/GeneralInput';
import GeneralSelect from '../../../main/components/select/GeneralSelect';
import IHotel from '../../../main/interfaces/IHotel';
import './RoomTypes.css';
import '../HotelShared/HotelShared.css';
import i18n from '../../../main/initializers/i18n';


interface IPagination {
  page: number;
  itemPerPage: number;
  count: number;
  itemsPerPage: number[];
}

interface IGridData {
  entity: string;
  label: string;
  isShown: boolean;
  sort?: string | null;
  filter?: {
    value?: string;
    operator?: string;
  } | null;
}

const defaultPagination: IPagination = {
  page: 1,
  itemPerPage: 10,
  count: 0,
  itemsPerPage: [10, 20, 50, 100],
};

const defaultColumns: IGridData[] = [
  { entity: 'hotel.name', label: 'Hotel', isShown: true },
  { entity: 'name', label: 'Name', isShown: true },
  { entity: 'code', label: 'Code', isShown: true },
  { entity: 'baseOccupancy', label: 'Base Occupancy', isShown: true },
  { entity: 'maxOccupancy', label: 'Max Occupancy', isShown: true },
  { entity: 'defaultNightPrice', label: 'Default Night Price', isShown: true },
  { entity: 'totalRooms', label: 'Rooms', isShown: true }
];

const emptyRoomType: IRoomType = {
  id: 0,
  hotelId: '',
  name: '',
  code: '',
  baseOccupancy: 1,
  maxOccupancy: 1,
  defaultNightPrice: 0,
  description: '',
};

interface RoomTypeDetailsProps {
  roomType: IRoomType;
  hotels: IHotel[];
  onClose: () => void;
  onSave: (payload: IRoomType) => void;
}

const RoomTypeDetails = ({ roomType, hotels, onClose, onSave }: RoomTypeDetailsProps) => {

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IRoomType>({
    defaultValues: roomType,
  });

  useEffect(() => {
    reset(roomType);
  }, [roomType, reset]);

  const submit = (data: IRoomType) => {
    onSave({
      ...roomType,
      ...data,
      hotelId: Number(data.hotelId),
      baseOccupancy: Number(data.baseOccupancy),
      maxOccupancy: Number(data.maxOccupancy),
      defaultNightPrice: Number(data.defaultNightPrice),
    });
  };

  return (
    <form className="roomTypeForm" onSubmit={handleSubmit(submit)}>
      <div className="roomTypeGrid">
        <GeneralSelect
          label={i18n.t('hotel.hotel')}
          name="hotelId"
          control={control}
          required
          data={hotels}
          value="name"
          preSelectedVal={i18n.t('hotel.selectHotel')}
          errors={errors}
        />

        <GeneralInput 
        label={i18n.t('generic.name')} 
        name="name" type="text" 
        control={control} 
        required errors={errors} 
        placeholder={i18n.t('generic.name')} 
        />

        <GeneralInput 
        label={i18n.t('generic.code')}
        name="code" 
        type="text" 
        control={control} 
        required errors={errors}
        placeholder={i18n.t('generic.code')} 
         />

        <GeneralInput 
        label={i18n.t('roomType.baseOccupancy')} 
        name="baseOccupancy" 
        type="number" 
        control={control} 
        required errors={errors} 
        placeholder={i18n.t('roomType.baseOccupancy')} 
        />

        <GeneralInput 
        label={i18n.t('roomType.maxOccupancy')} 
        name="maxOccupancy" 
        type="number" 
        control={control} 
        required errors={errors} 
        placeholder={i18n.t('roomType.maxOccupancy')} 
        />

        <GeneralInput 
        label={i18n.t('roomType.defaultNightPrice')} 
        name="defaultNightPrice" 
        type="number" 
        control={control} 
        required errors={errors} placeholder={i18n.t('roomType.defaultNightPrice')} 
        />
      </div>

      <div className="roomTypeDescription">
        <GeneralInput 
        label={i18n.t('generic.description')} 
        name="description" 
        type="text" 
        control={control} 
        errors={errors} 
        placeholder={i18n.t('generic.description')} />
      </div>

      <div className="roomTypeActions">
        <Button variant="outlined" onClick={onClose}>
          {i18n.t('generic.cancel')}
        </Button>

        <Button type="submit" variant="contained">
          {roomType.id ? i18n.t('generic.update') : i18n.t('generic.save')}
        </Button>
      </div>
    </form>
  );
};

export const RoomTypesContent = ({
  embedded = false,
  tabsBar,
}: {
  embedded?: boolean;
  tabsBar?: React.ReactNode;
}) => {
  const [roomTypes, setRoomTypes] = useState<IRoomType[]>([]);
  const [hotels, setHotels] = useState<IHotel[]>([]);
  const [pagination, setPagination] = useState<IPagination>(defaultPagination);
  const [gridItems, setGridItems] = useState<IGridData[]>(defaultColumns);
  const [selectedRoomType, setSelectedRoomType] = useState<IRoomType>(emptyRoomType);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const [hotelFilterId, setHotelFilterId] = useState<number | null>(null);

  const loadHotels = useCallback(async () => {
    const res = await axiosInstance.get(ApiConstants.hotel.hotels);
    setHotels(res.data || []);
  }, []);

  const loadRoomTypes = useCallback(async (pg?: IPagination, items?: IGridData[]) => {
    setLoading(true);

    try {
      const request = {
        pagination: pg ?? defaultPagination,
        gridItems: items ?? defaultColumns,
      };

      const res = await axiosInstance.post(ApiConstants.hotel.roomTypesListCt, request);

      setRoomTypes(res.data.items || []);
      setPagination({
        ...defaultPagination,
        ...(res.data.pagination || {}),
      });
      setGridItems(res.data.gridItems || defaultColumns);
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to load room types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHotels();
    loadRoomTypes(defaultPagination, defaultColumns);
  }, []);

  const filteredRoomTypes = hotelFilterId
    ? roomTypes.filter((r) => r.hotelId === hotelFilterId)
    : roomTypes;

  const handleOpenCreate = () => {
    setSelectedRoomType(emptyRoomType);
    setOpenModal(true);
  };

  const handleEdit = (item: IRoomType) => {
    setSelectedRoomType(item);
    setOpenModal(true);
  };

  const handleDelete = async (item: IRoomType) => {
    if (!window.confirm(i18n.t('generic.wantToDelete'))) return;

    try {
      await axiosInstance.delete(ApiConstants.hotel.roomTypeDelete, {
  params: { id: item.id }
});
      toast.success(i18n.t('roomType.deleted'));
      loadRoomTypes(defaultPagination, defaultColumns);
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to delete room type');
    }
  };

  const handleSave = async (payload: IRoomType) => {
    try {
      await axiosInstance.post(ApiConstants.hotel.roomTypeSave, payload);

      toast.success(payload.id ? i18n.t('roomType.updated') : i18n.t('roomType.created'));

      setOpenModal(false);
      setSelectedRoomType(emptyRoomType);

      loadRoomTypes(defaultPagination, defaultColumns);
    } catch (err: any) {
      toast.error(err?.response?.data || 'Failed to save room type');
    }
  };

  const handlePaginationChange = (pg: IPagination) => {
    setPagination(pg);
    loadRoomTypes(pg, gridItems);
  };

  return (
    <div className={`hotelSectionPage roomTypesPage ${embedded ? 'roomTypesPageEmbedded' : ''}`}>
      {!embedded && (
        <div className="ui-panel hotelSectionHeader roomTypesHeader">
          <div className="hotelSectionHeading">
            <h2>{i18n.t('roomType.title')}</h2>
            <p>{i18n.t('roomType.subtitle')}</p>
          </div>

          <div className="hotelSectionActions roomTypesHeaderActions">
            <div className="roomTypesFilter">
              <select
                className="ui-control ui-input"
                value={hotelFilterId ?? ''}
                onChange={(e) => setHotelFilterId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">{i18n.t('hotel.allHotels')}</option>

                {hotels.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>

            <Button
              variant="contained"
              className="addRoomTypeBtn"
              onClick={handleOpenCreate}
            >
              {i18n.t('roomType.addRoomType')}
            </Button>
          </div>
        </div>
      )}

      {embedded && (
        <div className="ui-panel hotelSectionHeader roomTypesHeader roomTypesHeaderEmbedded">
          <div className="hotelInfoEmbeddedRow">
            <div className="hotelInfoEmbeddedTabs">{tabsBar}</div>
            <div className="hotelSectionActions roomTypesHeaderActions">
              <div className="roomTypesFilter hotelInfoToolbarField">
                <select
                  className="ui-control ui-input"
                  value={hotelFilterId ?? ''}
                  onChange={(e) => setHotelFilterId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">{i18n.t('hotel.allHotels')}</option>

                  {hotels.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.name}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                variant="contained"
                className="addRoomTypeBtn hotelInfoPrimaryAction"
                onClick={handleOpenCreate}
              >
                {i18n.t('roomType.addRoomType')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="hotelSectionCard hotelTableCard roomTypesCard">
        {loading ? (
          <div className="roomTypesLoading">{i18n.t('generic.loading')}</div>
        ) : (
          <GeneralTable
            data={filteredRoomTypes || []}
            title={i18n.t('roomType.roomTypeList')}
            hasRp
            columns={gridItems || []}
            gridEntity={13}
            pagination={pagination}
            selectedItem={(item) => setSelectedRoomType(item)}
            selectedItemPagination={handlePaginationChange}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </div>

      {openModal && (
        <GeneralModal
          handleClose={() => setOpenModal(false)}
          headerName={
            selectedRoomType.id ? i18n.t('roomType.editRoomType') : i18n.t('roomType.createRoomType')
          }
          width="900px"
          radius="16px"
          hideFooter
        >
          <RoomTypeDetails
            roomType={selectedRoomType}
            hotels={hotels}
            onClose={() => setOpenModal(false)}
            onSave={handleSave}
          />
        </GeneralModal>
      )}
    </div>
  );
};

const RoomTypes = () => (
  <BasicCli>
    <RoomTypesContent />
  </BasicCli>
);

export default RoomTypes;
